import { Box, Typography, CircularProgress, Chip } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Module-level constants to avoid re-creation on every render
const REMARK_PLUGINS = [remarkGfm];

function MarkdownLink({ children, node: _node, ...props }) {
  const theme = useTheme();
  return (
    <a
      {...props}
      target="_blank"
      rel="noreferrer"
      style={{
        color: theme.palette.primary.main,
        textDecoration: "underline"
      }}
    >
      {children}
    </a>
  );
}

const MARKDOWN_COMPONENTS = {
  pre: ({ _node, ...props }) => (
    <pre style={{ whiteSpace: "pre-wrap" }} {...props} />
  ),
  code: ({ _node, inline, ...props }) => {
    if (inline) return <code {...props} />;
    return <code style={{ whiteSpace: "pre-wrap" }} {...props} />;
  },
  table: function MarkdownTable({ _node, children, ...props }) {
    return (
      <div style={{ overflowX: "auto", margin: "8px 0" }}>
        <table {...props}>{children}</table>
      </div>
    );
  },
  a: MarkdownLink
};

export default function MessageList({
  classes,
  messages,
  isStreaming,
  agentStatusUpdate,
  exampleQueries,
  emptyStateTitle,
  emptyStateDescription,
  onExampleClick
}) {
  return (
    <>
      {messages.length === 0 && !isStreaming ? (
        <Box className={classes.emptyState}>
          <Typography variant="h6" color="textPrimary" gutterBottom>
            {emptyStateTitle}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ whiteSpace: "pre-line" }}
          >
            {emptyStateDescription}
          </Typography>
          {exampleQueries.length > 0 && (
            <Box mt={2} display="flex" flexDirection="column" gap={1}>
              <Typography variant="caption" color="textSecondary">
                Try these examples:
              </Typography>
              {exampleQueries.map((query, idx) => (
                <Chip
                  key={idx}
                  label={query}
                  onClick={() => onExampleClick(query)}
                  clickable
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          )}
        </Box>
      ) : (
        messages.map((msg, idx) => {
          const isConsecutive = idx > 0 && messages[idx - 1].role === msg.role;
          const marginBottomStyle = isConsecutive ? 4 : 16;

          return (
            <Box
              key={msg.id}
              className={`${classes.messageBubble} ${
                msg.role === "user"
                  ? classes.userMessage
                  : classes.assistantMessage
              }`}
              style={{ marginBottom: marginBottomStyle }}
            >
              {msg.role === "user" ? (
                <Typography variant="body2">{msg.content}</Typography>
              ) : (
                <Box className={classes.markdownContent}>
                  <ReactMarkdown
                    remarkPlugins={REMARK_PLUGINS}
                    components={MARKDOWN_COMPONENTS}
                  >
                    {msg.content}
                  </ReactMarkdown>
                </Box>
              )}
            </Box>
          );
        })
      )}
      {isStreaming && agentStatusUpdate && (
        <Box className={classes.statusIndicator}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="textSecondary">
            {agentStatusUpdate}
          </Typography>
        </Box>
      )}
    </>
  );
}
